﻿import { Component, Input, Output, Inject } from "@fiqas/angular-utils";
import { FIQAssist } from "../../app";
import { TableCell, TableRow } from "../../controls/datatable";
import { CaseFileData } from "../../models/file";
import { FileReaderService } from "../../services/fileReaderService";
import { AttachmentLimit } from "../../constants";
import { DialogService } from "../../services/dialogService";
import { TranslationService } from "../../services/TranslationService";

interface CaseFileDataExt extends CaseFileData {
    filesize: number;
}

export class FileUploader {
    constructor(
        @Inject('$element') private $element: angular.IRootElementService,
        @Inject('$timeout') private $timeout: angular.ITimeoutService,
        @Inject('$scope') private $scope: angular.IScope,
        @Inject('$q') private $q: ng.IQService,
        @Inject('fileReaderService') private fileReaderService: FileReaderService,
        @Inject('AttachmentLimit') private attachmentLimit: number,
        @Inject() private dialogService: DialogService,
        @Inject() private translationService: TranslationService,
    ) {

    }

    private oldFilesLength = 0;

    @Input() public readOnly: boolean;

    $postLink() {
        let a = this.$element.find('input');
        a.on('change', this.onaddfile.bind(this));
    }


    @Input()
    public get files(): CaseFileData[] {
        if (this._files === undefined) {
            this._files = [];
        }

        if (this.oldFilesLength !== this._files.length) {
            this.updateDataTable();
            this.oldFilesLength = this._files.length;
        }

        return this._files;
    }

    public set files(files: CaseFileData[]) {
        this._files = files;
    }

    @Output()
    public onfileadded: (args: { file: CaseFileData }) => void;
    @Output()
    public onfileremoved: (args: { file: CaseFileData }) => void;

    private _files: CaseFileData[];
    private _dataTableData: TableRow[] = [];
    private hasfiles: boolean = false;

    private handleFilesExceedingSizeLimit(filelist: CaseFileData[]) {
        let FILE_TO_LARGE = this.translationService.Translate("FILE_TO_LARGE", false);
        let FILE_NON_CONTENT = this.translationService.Translate("FILE_NON_CONTENT", false);
        let FILE_UNKOWN_ERROR = this.translationService.Translate("FILE_UNKOWN_ERROR", false);
        let information: string[] = ["<ul>"];
        for (let file of filelist) {
            if ((<CaseFileDataExt>file).filesize > this.attachmentLimit * 1024 * 1024) {
                information.push(`<li>${file.filename}, ${FILE_TO_LARGE} (${((<CaseFileDataExt>file).filesize / 1024 / 1024).toFixed(2)} MB)</li>`);
            } else if ((<CaseFileDataExt>file).filesize == 0) {
                information.push(`<li>${file.filename}, ${FILE_NON_CONTENT} </li>`);
            } else {
                information.push(`<li>${file.filename}, ${FILE_UNKOWN_ERROR} </li>`);
            }
        }
        information.push("</ul>")
        this.dialogService.showErrorDialog("warning", 'ERROR', 'FILES_EXCEEDS_LIMIT', { messageArgs: [this.attachmentLimit.toFixed(2), information.join('')], translate: true });
    }


    private async onfiledrop(fileList: FileList) {
        if (this.readOnly) return;
        let faultedFiles: CaseFileData[] = [];
        let correctSizedFiles: File[] = [];
        this.split(fileList, this.attachmentLimit, correctSizedFiles, faultedFiles);
        let readFilePromises = this.makeReadFilePromises(correctSizedFiles);
        let newfiles = await Promise.all(readFilePromises)
        newfiles.forEach(x => {
            if (x.content !== "") {
                // files in the list are maintained by the @Input files
                //this.files.push(x);
                this.onfileadded({ file: x });
            } else {
                faultedFiles.push(x);
            }
        });
        this.updateDataTable();
        this.$timeout(() => {
            try {
                (<any>(this.$element[0].getElementsByClassName('dataTableContent')[0])).focus();
            } catch (ex) { }
        });
        if (faultedFiles.length > 0) {
            this.handleFilesExceedingSizeLimit(faultedFiles);
        }
    }

    private ontrash() {
        if (this.readOnly) return;
        let toDelete = this._dataTableData.filter(x => x.selected).map(y => y.id);
        this._dataTableData.filter(x => x.selected).forEach(x => {
            let remaining: CaseFileData[] = [];
            this._files.forEach(z => {

                if (!toDelete.some(x => x == z.filename)) {
                    remaining.push(z);
                } else {
                    this.onfileremoved({ file: z });
                }
            });
            // files in the list are maintained by the @Input files
            //this._files = remaining;
        });
        this.updateDataTable();
    }

    private onDelete(rows: TableRow[]) {
        if (this.readOnly) return;
        let ids = rows.map(x => x.id);
        let remaining: CaseFileData[] = [];
        this._files.forEach(z => {

            if (!(ids.some(x => x == z.filename))) {
                remaining.push(z);
            } else {
                this.onfileremoved({ file: z });
            }
        });
        // files in the list are maintained by the @Input files
        //this._files = remaining;
        this.updateDataTable();
    }

    private async onaddfile(event: Event) {
        if (this._files == undefined)
            this._files = [];
        let el = event.target || event.srcElement;
        let faultedFiles: CaseFileData[] = [];
        let correctSizedFiles: File[] = [];
        let fileList = <FileList>(el as any).files;
        this.split(fileList, this.attachmentLimit, correctSizedFiles, faultedFiles);
        let readFilePromises = this.makeReadFilePromises(correctSizedFiles);
        let newfiles = await Promise.all(readFilePromises);
        newfiles.forEach(x => {
            if (x.content !== "") {
                // files in the list are maintained by the @Input files
                //this._files.push(x);
                this.onfileadded({ file: x });
                this.updateDataTable();
            } else {
                faultedFiles.push(x);
            }
        });
        if (faultedFiles.length > 0) {
            this.handleFilesExceedingSizeLimit(faultedFiles);
        }
        (el as any).value = null;
    }
    /**
     * filters of large files.
     *
     * @private
     * @param {FileList} fileList the list of files
     * @param {number} sizeLimit in MB units; initialized with this.attachmentLimit; multiplied (* 1024 * 1024) for Bytes of MB units
     * @param {File[]} correctSizedFiles initialized with empty array; files in fileList will be added to here when below sizeLimit
     * @param {CaseFileData[]} faultedFiles initialized with empty array; files in fileList will be added to here when above sizeLimit
     * @memberof FileUploader
     */
    private split(fileList: FileList, sizeLimit: number, correctSizedFiles: File[], faultedFiles: CaseFileData[]): void {
        sizeLimit = (sizeLimit) ? sizeLimit * 1024 * 1024 : this.attachmentLimit * 1024 * 1024;
        if (!correctSizedFiles) correctSizedFiles = [];
        if (!faultedFiles) faultedFiles = [];
        for (let i = 0; i < fileList.length; i++) {
            let file: File = fileList.item(i);
            if (file.size >= sizeLimit) {
                faultedFiles.push(<CaseFileDataExt>{
                    filename: file.name,
                    description: file.name,
                    content: btoa(""),
                    filesize: file.size
                });
            } else {
                correctSizedFiles.push(file);
            }
        }
    }
    private makeReadFilePromises(files: File[]): ng.IPromise<CaseFileData>[] {
        return files.map(x => this.fileReaderService.ReadAsBinaryString(x).then(y => {
            if (x.size < this.attachmentLimit * 1024 * 1024) {
                return <CaseFileDataExt>{
                    filename: x.name,
                    description: x.name,
                    content: btoa(<string>y),
                    filesize: x.size
                };
            } else {
                return <CaseFileDataExt>{
                    filename: x.name,
                    description: x.name,
                    content: btoa(""),
                    filesize: x.size
                };
            }
        }
        ));
    }

    private updateDataTable() {
        this.hasfiles = false;
        if (this._files != null) {
            let newData = this._files.map(item => {
                this.hasfiles = true;
                let row = new TableRow(item.filename, new Array<TableCell>());
                row.cells.push(new TableCell(item.filename));
                if ((<CaseFileDataExt>item).filesize) {
                    row.cells.push(new TableCell((<CaseFileDataExt>item).filesize.toString() + ' Bytes'));
                } else {
                    row.cells.push(new TableCell(item.content.length + ' Bytes'));
                }

                return row;
            });
            this._dataTableData = newData;
        } else {
            this._dataTableData = [];
        }
    }

    private async onFileSelectorKey() {
        if (this.readOnly)
            return;
        await this.$timeout()
        this.$element.find("input")[0].click();
    }

}

@Component({
    module: FIQAssist,
    selector: "fileuploader",
    templateUrl: "views/fileUploader.html"
})
export class FileUploaderExt extends FileUploader {
}

@Component({
    module: FIQAssist,
    selector: "fileuploadbutton",
    templateUrl: "views/fileUploadButton.html"
})
export class FileUploaderExt2 extends FileUploader {
}
